import React, { useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODELO_NOTA = `Se você se cadastrou e não conseguiu ver o contato de uma vaga, a falha era nossa — e já foi corrigida. ✅ Agora é simples: toque no link da vaga, entre na sua conta e informe uma referência de trabalho para ver o contato na hora. Quem informa referência também ganha mais visibilidade para as fazendas. Vagas abertas: heveatalent.com.br/vagas 🌳`;

const AUDIENCES = [
  { value: "all", label: "Todos os usuários" },
  { value: "professional", label: "Só profissionais" },
  { value: "employer", label: "Só proprietários" },
];

const ATTACH_OPTIONS = [
  { value: "none", label: "💼 Não anexar vagas" },
  { value: "selected", label: "💼 Vagas selecionadas" },
  { value: "all", label: "💼 TODAS as vagas" },
];

const ATTACH_LABOR_OPTIONS = [
  { value: "none", label: "🤝 Não anexar profissionais" },
  { value: "selected", label: "🤝 Profissionais selecionados" },
  { value: "all", label: "🤝 TODOS os profissionais" },
];

export const AdminBroadcastCard = ({ selectedJobs = [], allJobs = [], selectedLabor = [], allLabor = [] }) => {
  const [title, setTitle] = useState("📢 Comunicado HeveaTalent");
  const [message, setMessage] = useState("");
  const [audience, setAudience] = useState("all");
  const [attach, setAttach] = useState("none");
  const [attachLabor, setAttachLabor] = useState("none");
  const [sending, setSending] = useState(false);

  const attachedJobs = attach === "selected" ? selectedJobs : attach === "all" ? allJobs : [];
  const attachedLabor = attachLabor === "selected" ? selectedLabor : attachLabor === "all" ? allLabor : [];

  const send = async () => {
    const alvo = AUDIENCES.find((a) => a.value === audience)?.label || "";
    const extras = [];
    if (attachedJobs.length > 0) extras.push(`${attachedJobs.length} vaga(s)`);
    if (attachedLabor.length > 0) extras.push(`${attachedLabor.length} profissiona${attachedLabor.length > 1 ? "is" : "l"}`);
    const extra = extras.length > 0 ? ` com ${extras.join(" + ")} anexado(s)` : "";
    if (!window.confirm(`Enviar este comunicado${extra} no sininho de "${alvo}"? Não dá para desfazer.`)) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/admin/broadcast`, {
        title, message, audience,
        job_ids: attachedJobs.map((j) => j.id),
        labor_ids: attachedLabor.map((o) => o.id),
      });
      toast.success(`Comunicado enviado para ${res.data.sent} usuário(s)! 🔔`);
      setAttach("none");
      setAttachLabor("none");
      setMessage("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao enviar o comunicado");
    } finally {
      setSending(false);
    }
  };

  return (
    <Card className="border-sky-200" data-testid="admin-broadcast-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">🔔 Comunicado no sininho</CardTitle>
        <p className="text-xs text-muted-foreground">
          Envia um aviso para o sino de notificações dos usuários da plataforma. Cole aqui a prévia do dia (ex: notícia do Radar) e, se quiser, anexe as vagas — elas saem automaticamente no final da mensagem.
        </p>
      </CardHeader>
      <CardContent className="space-y-2">
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Título do comunicado"
          data-testid="broadcast-note-title"
        />
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className="w-full text-sm border rounded-lg p-3 h-32"
          placeholder="Escreva ou cole aqui a mensagem/prévia do comunicado..."
          data-testid="broadcast-note-message"
        />
        <div className="flex gap-2">
          <Button type="button" size="sm" variant="outline" onClick={() => setMessage(MODELO_NOTA)} data-testid="broadcast-note-use-template">
            📋 Usar modelo
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={() => setMessage("")} disabled={!message} data-testid="broadcast-note-clear">
            🧹 Limpar
          </Button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <div>
            <p className="text-[11px] font-semibold text-gray-600 mb-1">👥 Enviar para (destinatários)</p>
            <Select value={audience} onValueChange={setAudience}>
              <SelectTrigger className="w-full" data-testid="broadcast-note-audience">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {AUDIENCES.map((a) => (
                  <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <p className="text-[11px] font-semibold text-gray-600 mb-1">📎 Anexar vagas? (opcional)</p>
            <Select value={attach} onValueChange={setAttach}>
              <SelectTrigger className="w-full" data-testid="broadcast-note-attach">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ATTACH_OPTIONS.map((a) => (
                  <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <p className="text-[11px] font-semibold text-gray-600 mb-1">📎 Anexar profissionais? (opcional)</p>
            <Select value={attachLabor} onValueChange={setAttachLabor}>
              <SelectTrigger className="w-full" data-testid="broadcast-note-attach-labor">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ATTACH_LABOR_OPTIONS.map((a) => (
                  <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <Button
          onClick={send}
          disabled={sending || !title.trim() || !message.trim()}
          className="bg-sky-600 hover:bg-sky-700 w-full sm:w-auto"
          data-testid="broadcast-note-send"
        >
          {sending ? "Enviando..." : "🔔 Enviar comunicado"}
        </Button>
        {(attach !== "none" || attachLabor !== "none") && (
          <p className="text-xs text-sky-800 bg-sky-50 border border-sky-200 rounded-lg px-3 py-2" data-testid="broadcast-note-attach-info">
            {attachedJobs.length > 0 || attachedLabor.length > 0 ? (
              <>
                {attachedJobs.length > 0 && `💼 ${attachedJobs.length} vaga(s)`}
                {attachedJobs.length > 0 && attachedLabor.length > 0 && " + "}
                {attachedLabor.length > 0 && `🤝 ${attachedLabor.length} profissiona${attachedLabor.length > 1 ? "is" : "l"}`}
                {" serão anexados ao final da mensagem."}
              </>
            ) : attach === "selected" || attachLabor === "selected" ? (
              "⚠️ Nada selecionado — marque as caixinhas ☑️ nas listas abaixo (vagas e/ou profissionais)."
            ) : (
              "⚠️ Nada ativo para anexar."
            )}
          </p>
        )}
      </CardContent>
    </Card>
  );
};
